import { Spinner } from '@app/components/icons/spinner';
import { reduceSegmentToSegmentGroups } from '@app/routes/content/routes/recordings/components/contex-menu/transcript/utils';
import {
  AdvancedEditDraft,
  TranscriptSegmentGroup,
  WordCorrection,
} from '@app/routes/content/routes/recordings/domain';
import {
  selectActiveAdvancedEdit,
  selectFilteredTranscriptSegments,
  selectIsEditFrozen,
  selectRecording,
  selectRecordingTranscript,
  selectRecordingTranscriptionStatus,
} from '@app/routes/content/routes/recordings/selector';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import { useTranslation } from '@hopin-team/provider-translation';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { VariableSizeList } from 'react-window';

import { EditTranscriptHeader } from './header-section';
import { useEffectiveTranscript } from './hooks/use-effective-transcript';
import { useWordSelection } from './hooks/use-word-selection';
import { EditSegmentGroup, getEditSegmentGroupSize } from './segment-group';
import { type FlatWord } from './utils/build-flat-words';
import { restoreGap, splitCutRange } from './utils/split-cut';
import { type PopupPosition, WordActionPopup } from './word-action-popup';

// ── Types ────────────────────────────────────────────────────────────────────

type PopupState = {
  visible: boolean;
  position: PopupPosition;
  selectedWords: FlatWord[];
  cutGapWords: FlatWord[];
};

const INITIAL_POPUP_STATE: PopupState = {
  visible: false,
  position: { x: 0, y: 0 },
  selectedWords: [],
  cutGapWords: [],
};

const DEFAULT_DRAFT: AdvancedEditDraft = {
  title: '',
  cuts: [],
  captions: { enabled: false },
  aspectRatio: '16_9',
  backgroundColor: null,
  wordCorrections: [],
};

// ── Word slice builder ───────────────────────────────────────────────────────

/**
 * For each segment group, returns the offset + count into the global flatWords
 * array so the renderer can slice groupFlatWords without re-running the full
 * build.
 */
const buildGroupWordSlices = (
  segmentGroups: TranscriptSegmentGroup[],
  flatWords: FlatWord[],
): Array<{ offset: number; count: number }> => {
  const seenSegIdx = new Set<number>();
  const segIdxOrder: number[] = [];
  for (const fw of flatWords) {
    if (!seenSegIdx.has(fw.segmentIndex)) {
      seenSegIdx.add(fw.segmentIndex);
      segIdxOrder.push(fw.segmentIndex);
    }
  }

  const segIdxToPos = new Map<number, number>();
  segIdxOrder.forEach((si, pos) => segIdxToPos.set(si, pos));

  const slices: Array<{ offset: number; count: number }> = [];
  let posInSegIdxOrder = 0;

  for (const group of segmentGroups) {
    const groupSegCount = group.segments.length;
    const groupSegPositions = new Set<number>();
    for (let i = 0; i < groupSegCount; i++) {
      groupSegPositions.add(posInSegIdxOrder + i);
    }
    posInSegIdxOrder += groupSegCount;

    let offset = -1;
    let count = 0;

    for (let fi = 0; fi < flatWords.length; fi++) {
      const fw = flatWords[fi];
      const pos = segIdxToPos.get(fw.segmentIndex);
      if (pos !== undefined && groupSegPositions.has(pos)) {
        if (offset === -1) offset = fi;
        count++;
      }
    }

    slices.push({ offset: offset === -1 ? 0 : offset, count });
  }

  return slices;
};

// Stable no-op passed to EditSegmentGroup when the edit is frozen so we don't
// need to make those props optional or change the component's interface.
const _noop = () => {
  return null;
};

// ── Main component ────────────────────────────────────────────────────────────

/**
 * Word-level transcript editor for the advanced edit view (editor-v2).
 *
 * Renders the filtered transcript segments as individually clickable/selectable
 * word spans. Supports:
 *   - Word removal / multi-word removal ("Remove from video")
 *   - Gap restoration ("Restore to video") when clicking a struck-through word
 *   - Word correction (inline editing inside the popup)
 *   - Strikethrough for cut words
 *   - Active playback word highlighting
 */
export const EditTranscript = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // ── Selectors ──
  const { status: fetchTranscriptStatus } = useSelector(
    selectRecordingTranscript,
  );
  const transcriptionStatus = useAppSelector(
    selectRecordingTranscriptionStatus,
  );
  const filteredSegments = useAppSelector(selectFilteredTranscriptSegments);
  const activeEdit = useAppSelector(selectActiveAdvancedEdit);
  const recording = useAppSelector(selectRecording);
  const isFrozen = useAppSelector(selectIsEditFrozen);

  const editId = activeEdit?.id ?? '';
  const currentDraft = activeEdit?.draft ?? DEFAULT_DRAFT;
  const cuts = currentDraft.cuts;

  // Recording duration in ms (for gap restoration)
  const recordingDurationMs = (recording?.duration ?? 0) * 1000;

  // ── Effective transcript (shared hook — same instance used by the header) ──
  const { flatWords, wordCorrections } = useEffectiveTranscript();

  // ── Segment groups + word slices ──
  const segmentGroups = useMemo(
    () => reduceSegmentToSegmentGroups(filteredSegments),
    [filteredSegments],
  );

  const groupWordSlices = useMemo(
    () => buildGroupWordSlices(segmentGroups, flatWords),
    [segmentGroups, flatWords],
  );

  // ── Selection ──
  const {
    handleWordMouseDown,
    handleWordMouseEnter,
    handleMouseUp,
    clearSelection,
    selectedRange,
    selectedWords,
    cutGapWords,
    highlightedIndices,
  } = useWordSelection(flatWords, cuts);

  // ── Popup state ──
  const [popupState, setPopupState] = useState<PopupState>(INITIAL_POPUP_STATE);

  // Show popup after mouseup if we have any kind of selection.
  // No-op when frozen — the edit is PROCESSING or COMPLETED.
  const handleContainerMouseUp = useCallback(
    (e: React.MouseEvent) => {
      handleMouseUp();
      if (isFrozen) return;

      const hasSelection = selectedWords.length > 0 || cutGapWords.length > 0;
      if (!hasSelection) return;

      // Determine anchor element for positioning
      const anchorIdx =
        cutGapWords.length > 0
          ? // For gap, anchor to the last word in the gap
            highlightedIndices.size > 0
            ? Math.max(...highlightedIndices)
            : 0
          : selectedRange?.end ?? 0;

      const wordEl = document.querySelector<HTMLElement>(
        `[data-flat-idx="${anchorIdx}"]`,
      );

      let x = e.clientX;
      let y = e.clientY;

      if (wordEl) {
        const rect = wordEl.getBoundingClientRect();
        // x — centred on the last highlighted word
        x = rect.left + rect.width / 2;
        // y — bottom edge of the last highlighted word so the popup
        // appears just below it (8px gap added in word-action-popup.tsx)
        y = rect.bottom;
      }

      setPopupState({
        visible: true,
        position: { x, y },
        selectedWords,
        cutGapWords,
      });
    },
    [
      handleMouseUp,
      isFrozen,
      selectedWords,
      cutGapWords,
      selectedRange,
      highlightedIndices,
    ],
  );

  // ── Popup actions ────────────────────────────────────────────────────────

  /** Remove single or multi-word selection from the video (adds to cuts). */
  const handleRemoveFromVideo = useCallback(() => {
    if (!recording || popupState.selectedWords.length === 0) return;

    const first = popupState.selectedWords[0];
    const last = popupState.selectedWords[popupState.selectedWords.length - 1];
    const startMs = first.word.start * 1000;
    const endMs = last.word.end * 1000;
    const newCuts = splitCutRange(cuts, startMs, endMs, recording.id);

    dispatch({
      type: '[advanced-editor] edit draft changed',
      payload: {
        editId,
        draft: { ...currentDraft, cuts: newCuts },
        previousDraft: currentDraft,
      },
    });

    clearSelection();
    setPopupState(INITIAL_POPUP_STATE);
  }, [
    recording,
    popupState.selectedWords,
    cuts,
    editId,
    currentDraft,
    dispatch,
    clearSelection,
  ]);

  /** Restore the entire deleted gap containing the clicked cut word. */
  const handleRestoreToVideo = useCallback(() => {
    if (popupState.cutGapWords.length === 0) return;

    const clickedWord = popupState.cutGapWords[0];
    const newCuts = restoreGap(
      currentDraft.cuts,
      clickedWord.word.start * 1000,
      clickedWord.word.end * 1000,
      recordingDurationMs,
    );

    dispatch({
      type: '[advanced-editor] edit draft changed',
      payload: {
        editId,
        draft: { ...currentDraft, cuts: newCuts },
        previousDraft: currentDraft,
      },
    });

    setPopupState(INITIAL_POPUP_STATE);
    clearSelection();
  }, [
    popupState.cutGapWords,
    currentDraft,
    recordingDurationMs,
    editId,
    dispatch,
    clearSelection,
  ]);

  /** Save a word correction (or revert) and close the popup. */
  const handleSaveCorrection = useCallback(
    (newCorrections: WordCorrection[]) => {
      // Single dispatch: replaces the full draft (including wordCorrections),
      // triggers the auto-save debounce, and pushes to the undo stack.
      // A separate '[advanced-editor] word corrections updated' dispatch is
      // not needed — the draft replacement already covers it.
      dispatch({
        type: '[advanced-editor] edit draft changed',
        payload: {
          editId,
          draft: { ...currentDraft, wordCorrections: newCorrections },
          previousDraft: currentDraft,
        },
      });
      clearSelection();
    },
    [editId, currentDraft, dispatch, clearSelection],
  );

  const handleClosePopup = useCallback(() => {
    setPopupState(INITIAL_POPUP_STATE);
    clearSelection();
  }, [clearSelection]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        clearSelection();
        setPopupState(INITIAL_POPUP_STATE);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [clearSelection]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const onMouseUp = (e: MouseEvent) =>
      handleContainerMouseUp(e as unknown as React.MouseEvent);
    node.addEventListener('mouseup', onMouseUp);
    return () => node.removeEventListener('mouseup', onMouseUp);
  }, [handleContainerMouseUp]);

  // ── Virtual list ──
  const listRef = useRef<VariableSizeList>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(0);

  const getGroupSize = useCallback(
    (index: number) =>
      getEditSegmentGroupSize(
        segmentGroups[index],
        containerRef.current?.clientWidth ?? 372,
      ),
    [segmentGroups],
  );

  useLayoutEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    let rafId: number | null = null;
    let lastHeight: number | null = null;

    const setupObserver = () => {
      const container = containerRef.current;
      if (!container) return;

      const initialHeight = container.clientHeight;
      if (initialHeight > 0) {
        setContainerHeight(initialHeight);
        lastHeight = initialHeight;
      }

      resizeObserver = new ResizeObserver(entries => {
        if (rafId !== null) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          const entry = entries.find(e => e.target === container);
          if (entry) {
            const { height } = entry.contentRect;
            if (lastHeight === null || Math.abs(height - lastHeight) > 0.5) {
              lastHeight = height;
              setContainerHeight(height);
            }
          }
          rafId = null;
        });
      });

      resizeObserver.observe(container);
    };

    requestAnimationFrame(setupObserver);
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0, true);
    }
  }, [containerHeight, segmentGroups, flatWords]);

  // ── Row renderer ──
  const GroupRenderer = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const group = segmentGroups[index];
      const slice = groupWordSlices[index];
      if (!group || !slice) return null;

      const groupFlatWords = flatWords.slice(
        slice.offset,
        slice.offset + slice.count,
      );

      return (
        <div style={style}>
          <div className="mx-4">
            <EditSegmentGroup
              group={group}
              groupIndex={index}
              groupFlatWords={groupFlatWords}
              flatIndexOffset={slice.offset}
              highlightedIndices={highlightedIndices}
              currentTimeSeconds={0}
              onWordMouseDown={isFrozen ? _noop : handleWordMouseDown}
              onWordMouseEnter={isFrozen ? _noop : handleWordMouseEnter}
            />
          </div>
        </div>
      );
    },
    [
      isFrozen,
      segmentGroups,
      groupWordSlices,
      flatWords,
      highlightedIndices,
      handleWordMouseDown,
      handleWordMouseEnter,
    ],
  );

  // ── Status state flags ──
  const isTranscriptionNotReady = transcriptionStatus !== 'ready';
  const isTranscriptLoading = fetchTranscriptStatus === 'processing';
  const hasSegmentGroups = segmentGroups.length > 0;

  return (
    <div className="flex flex-col gap-3 h-full">
      <EditTranscriptHeader />
      {/* When frozen (PROCESSING or COMPLETED) interactions are disabled.
          select-none prevents accidental text selection; cursor-default
          signals to the user that the transcript is read-only. */}
      <div
        ref={containerRef}
        className={`flex-1 w-full min-h-0 pb-4 relative${
          isFrozen ? ' select-none cursor-default opacity-60' : ''
        }`}
      >
        {isTranscriptionNotReady ? (
          <TranscriptionNotReady status={transcriptionStatus} t={t} />
        ) : isTranscriptLoading ? (
          <TranscriptLoading />
        ) : !hasSegmentGroups ? (
          <NoTranscriptFound t={t} />
        ) : (
          <VariableSizeList
            ref={listRef}
            height={containerHeight}
            width="100%"
            itemCount={segmentGroups.length}
            itemSize={getGroupSize}
            className="[scrollbar-width:thin] [scrollbar-color:#696e7c_transparent]"
          >
            {GroupRenderer}
          </VariableSizeList>
        )}

        {/* Floating action popup */}
        {popupState.visible && (
          <WordActionPopup
            selectedWords={popupState.selectedWords}
            cutGapWords={popupState.cutGapWords}
            position={popupState.position}
            onRemoveFromVideo={handleRemoveFromVideo}
            onRestoreToVideo={handleRestoreToVideo}
            onSaveCorrection={handleSaveCorrection}
            onClose={handleClosePopup}
            wordCorrections={wordCorrections}
          />
        )}
      </div>
    </div>
  );
};

// ── Status sub-components ────────────────────────────────────────────────────

const TranscriptionNotReady = ({
  status,
  t,
}: {
  status: string | undefined;
  t: (key: string) => string;
}) => (
  <div
    className="flex flex-col items-center justify-center py-12 text-center"
    style={{ height: '400px' }}
  >
    <h3 className="text-sm font-medium text-white mb-1 leading-5">
      {t(
        `content-recording.content-menu.transcription-status.${
          status || 'unknown'
        }`,
      )}
    </h3>
  </div>
);

const TranscriptLoading = () => (
  <div
    className="flex flex-col items-center justify-center py-12 text-center"
    style={{ height: '300px' }}
  >
    <Spinner size="medium" showLabel />
  </div>
);

const NoTranscriptFound = ({ t }: { t: (key: string) => string }) => (
  <div
    className="flex flex-col items-center justify-center py-12 text-center"
    style={{ height: '400px' }}
  >
    <h3 className="text-sm font-medium text-white mb-1 leading-5">
      {t('content-recording.content-menu.no-transcript-found')}
    </h3>
  </div>
);
